import React, { useState, useRef } from 'react';
import { Plus, Pencil, Trash2, X, Upload, Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import AdminLayout from '../../components/AdminLayout';

const blank = { id: '', name: '', tag: 'Hourly', price: 399, image: '', icon: 'Sparkles' };
const ICONS = ['Home', 'Sparkles', 'Building2', 'BedDouble', 'PanelTop', 'Droplet', 'Briefcase', 'Store', 'Users', 'Wrench', 'Zap'];

const AdminCategories = () => {
  const { categories, upsertCategory, deleteCategory } = useApp();
  const [editing, setEditing] = useState(null);
  const fileRef = useRef();

  const openNew = () => setEditing({ ...blank, id: 'cat_' + Date.now(), isNew: true });
  const openEdit = (c) => setEditing({ ...c, isNew: false });
  const close = () => setEditing(null);

  const save = () => {
    if (!editing.name.trim()) { alert('Name is required'); return; }
    const { isNew, ...cat } = editing;
    upsertCategory({ ...cat, price: Number(cat.price) || 0 });
    close();
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setEditing((prev) => ({ ...prev, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const remove = (id) => {
    if (window.confirm('Delete this category and all its services?')) deleteCategory(id);
  };

  return (
    <AdminLayout title="Categories">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-neutral-600">Manage service categories. Change photos, names, prices.</p>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#2E7D32] hover:bg-[#276D2A] text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors">
          <Plus className="w-4 h-4" /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {categories.map((c) => (
          <div key={c.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
            <div className="relative h-36 bg-neutral-100">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1">
                <button onClick={() => openEdit(c)} className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-white transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(c.id)} className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="p-3">
              <p className="font-bold text-sm">{c.name}</p>
              <p className="text-xs text-neutral-500 mt-0.5">{c.tag} • from ₹{c.price}</p>
              <p className="text-[10px] text-neutral-400 mt-1 font-mono">{c.id}</p>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="font-bold text-lg">{editing.isNew ? 'New Category' : 'Edit Category'}</h3>
              <button onClick={close} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Image</label>
                <div className="mt-1 flex gap-3">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                    {editing.image && <img src={editing.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input type="text" placeholder="Or paste image URL" value={editing.image}
                      onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:outline-none focus:border-[#2E7D32]" />
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
                    <button onClick={() => fileRef.current?.click()} className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-sm font-semibold text-neutral-700 transition-colors">
                      <Upload className="w-3.5 h-3.5" /> Upload from device
                    </button>
                  </div>
                </div>
              </div>

              <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tag" value={editing.tag} onChange={(v) => setEditing({ ...editing, tag: v })} />
                <Field label="Starting price (₹)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} />
              </div>

              <div>
                <label className="text-xs font-semibold text-neutral-700">Icon</label>
                <select value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:outline-none focus:border-[#2E7D32]">
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
            <div className="p-5 border-t border-neutral-200 flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-semibold text-neutral-700 hover:bg-neutral-100 transition-colors">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] text-white text-sm font-semibold transition-colors">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const Field = ({ label, type = 'text', value, onChange }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-700">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:outline-none focus:border-[#2E7D32]" />
  </div>
);

export default AdminCategories;
